import { QSET_REVIEW_TASK_DETAIL_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { WorkflowRole } from '@exam-management/common/types';
import {
  GetReviewQSetSubtaskDetailsByIdDocument,
  GetTaskByIdDocument,
  useUpdateTaskStatusByIdMutation,
  WorkflowStepCode,
} from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import {
  TaskOverdueDialog,
  TaskOverdueNoticeModal,
  isTaskOverdueError,
} from '@exam-management/task-management';
import {
  ConfirmationModal,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
  PersonRemoveOutlinedIcon,
  PersonOutlinedIcon,
  RateReviewOutlinedIcon,
  CheckCircleOutlineRoundedIcon,
  ReplyOutlinedIcon,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useMemo, useState } from 'react';

import { useQSetReviewTask } from '../../providers';

export interface QSetSubtaskMenusProps {
  workflowStepCode: WorkflowStepCode;
  onRemoveReviewer: () => void;
  onChangeReviewer: () => void;
  subtaskId: string;
  shouldShowRemoveReviewer: boolean;
  isAssignedToSubtask: boolean;
}

export default function QSetSubtaskMenus(props: Readonly<QSetSubtaskMenusProps>) {
  const logger = useLogger();
  const {
    onRemoveReviewer,
    onChangeReviewer,
    workflowStepCode,
    subtaskId,
    shouldShowRemoveReviewer,
    isAssignedToSubtask,
  } = props;
  const { showSnackbar } = useSnackbar();
  const { assignedRoles } = useQSetReviewTask();
  const navigate = useNavigate();
  const { taskId } = useParams('/my-task/qset-review/:taskId');

  const [isMarkAsDoneModalOpen, { open: openMarkAsDoneModal, close: closeMarkAsDoneModal }] =
    useDisclosure(false);
  const [isSendBackModalOpen, { open: openSendBackModal, close: closeSendBackModal }] =
    useDisclosure(false);
  const [overdueAction, setOverdueAction] = useState<'start' | 'mark-as-done' | 'send-back' | null>(
    null,
  );

  const isTaskOwner = assignedRoles.includes(WorkflowRole.TASK_OWNER);
  const isLeadReviewer = assignedRoles.includes(WorkflowRole.TASK_ASSIGNEE);

  const [updateTaskStatus] = useUpdateTaskStatusByIdMutation({
    refetchQueries: [GetTaskByIdDocument, GetReviewQSetSubtaskDetailsByIdDocument],
    awaitRefetchQueries: true,
  });

  const handleUpdateTaskStatus = async (
    workflowStepId: string,
    successMessage: string,
    errorMessage: string,
    action: 'start' | 'mark-as-done' | 'send-back',
  ) => {
    let isSuccess = false;
    await updateTaskStatus({
      variables: {
        updateTaskStatusInput: {
          taskId: subtaskId,
          workflowStepId,
        },
      },
      onCompleted: () => {
        isSuccess = true;
        showSnackbar({
          message: successMessage,
          variant: 'success',
        });
      },
      onError: (error) => {
        if (isTaskOverdueError(error)) {
          setOverdueAction(action);
          return;
        }
        logger.error('Failed to update task status:', error);
        showSnackbar({
          message: errorMessage,
          variant: 'error',
        });
      },
    });
    return isSuccess;
  };

  const menuOptions: MoreMenuIconButtonOption[] = useMemo(() => {
    const options: MoreMenuIconButtonOption[] = [];

    switch (workflowStepCode) {
      case WorkflowStepCode.ReviewQsetTodo:
        if (isLeadReviewer && !isTaskOwner && isAssignedToSubtask) {
          options.push({
            label: 'Start Review',
            icon: <RateReviewOutlinedIcon />,
            'data-testid': QSET_REVIEW_TASK_DETAIL_DATA_TEST_ID.START_REVIEW_MENU_ITEM,
            onClick: async () => {
              const isSuccess = await handleUpdateTaskStatus(
                'q-set-review-in-review',
                'Subtask is now In Review',
                'Failed to start review. Please try again.',
                'start',
              );
              if (!isSuccess) {
                return;
              }
              navigate(`/my-task/qset-review/:taskId/subtask/:subTaskId`, {
                params: {
                  taskId,
                  subTaskId: subtaskId,
                },
              });
            },
          });
        }
        options.push({
          label: 'Change Reviewer',
          icon: <PersonOutlinedIcon />,
          onClick: onChangeReviewer,
          'data-testid': QSET_REVIEW_TASK_DETAIL_DATA_TEST_ID.CHANGE_REVIEWER_MENU_ITEM,
        });
        if (shouldShowRemoveReviewer) {
          options.push({
            label: 'Remove Reviewer',
            icon: <PersonRemoveOutlinedIcon />,
            onClick: onRemoveReviewer,
          });
        }
        break;
      case WorkflowStepCode.ReviewQsetInReview:
        options.push({
          label: 'Change Reviewer',
          icon: <PersonOutlinedIcon />,
          onClick: onChangeReviewer,
          'data-testid': QSET_REVIEW_TASK_DETAIL_DATA_TEST_ID.CHANGE_REVIEWER_MENU_ITEM,
        });
        break;
      case WorkflowStepCode.ReviewQsetReviewed:
        if (isTaskOwner) {
          options.push({
            label: 'Mark as Done',
            icon: <CheckCircleOutlineRoundedIcon />,
            onClick: () => openMarkAsDoneModal(),
            'data-testid': QSET_REVIEW_TASK_DETAIL_DATA_TEST_ID.MARK_AS_DONE_MENU_ITEM,
          });
          options.push({
            label: 'Send Back',
            icon: <ReplyOutlinedIcon />,
            onClick: () => openSendBackModal(),
          });
        }
        break;
    }

    return options;
  }, [
    onRemoveReviewer,
    onChangeReviewer,
    workflowStepCode,
    isLeadReviewer,
    isTaskOwner,
    isAssignedToSubtask,
    shouldShowRemoveReviewer,
    handleUpdateTaskStatus,
    navigate,
    taskId,
    subtaskId,
  ]);

  if (workflowStepCode === WorkflowStepCode.ReviewQsetDone) {
    return null;
  }

  if (menuOptions.length === 0) return null;

  return (
    <>
      <MoreMenuIconButton options={menuOptions} />

      <ConfirmationModal
        open={isMarkAsDoneModalOpen}
        onClose={() => closeMarkAsDoneModal()}
        title='Mark as Done?'
        content='คุณต้องการเปลี่ยนสถานะของ Subtask นี้เป็น Done หรือไม่?'
        acceptText='Mark as Done'
        onAccept={async () => {
          await handleUpdateTaskStatus(
            'q-set-review-done',
            'Task has been marked as Done',
            'Failed to mark task as Done. Please try again.',
            'mark-as-done',
          );
          closeMarkAsDoneModal();
        }}
        onDiscard={() => closeMarkAsDoneModal()}
      />

      <ConfirmationModal
        open={isSendBackModalOpen}
        onClose={() => closeSendBackModal()}
        title='Send Back?'
        content='คุณต้องการส่ง Subtask นี้กลับไปเพื่อแก้ไขหรือทบทวนใหม่หรือไม่?'
        acceptText='Send Back'
        onAccept={async () => {
          await handleUpdateTaskStatus(
            'q-set-review-todo',
            'Task has been sent back',
            'Failed to send back task. Please try again.',
            'send-back',
          );
          closeSendBackModal();
        }}
        onDiscard={() => closeSendBackModal()}
      />

      <TaskOverdueNoticeModal
        open={overdueAction === 'start'}
        onClose={() => setOverdueAction(null)}
      />
      <TaskOverdueDialog
        variant={overdueAction === 'send-back' ? 'send-back' : 'mark-as-done'}
        open={overdueAction === 'mark-as-done' || overdueAction === 'send-back'}
        onClose={() => setOverdueAction(null)}
      />
    </>
  );
}
